/**
 * Feature E02-F02 — project DETAIL card typed api-client + Query hooks (T02/T03/T05/T06).
 *
 * Feature-owned (the shared `api/queries.ts` only has the LIST hooks). Uses the
 * shared `apiClient` (auto-injects the session Bearer token, normalizes the
 * response, never throws) + `unwrap` (the single throw-at-the-Query-boundary
 * helper). Contracts come from `@eer/shared` so the SPA speaks the exact shapes
 * the Fastify routes validate.
 *
 * Endpoints (packages/api/src/routes/projects.ts):
 *   GET   /projects/:id   → ProjectDetailCard | { success:false, error } (T02)
 *   PATCH /projects/:id   { updates } → projectDetailResult (T03/T04/T05/T06)
 *   GET   /clients        → ClientOption[]  (autocomplete cache, T05)
 *
 * The detail card is validated against `projectDetailCardSchema` at the boundary
 * (a drifted shape fails loudly), and the failure envelope is surfaced as a thrown
 * `ApiResultError` so the view renders a not-found state.
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  projectDetailCardResponseSchema,
  clientsCacheSchema,
  assignSourcingTeamResponseSchema,
  stageChangeResultSchema,
  discardDraftResultSchema,
  type ProjectDetailCard,
  type ProjectUpdates,
  type ClientOption,
  type AssignSourcingTeamResponse,
  type StageChangeResult,
} from '@eer/shared';
import { apiClient, unwrap, ApiResultError, type ApiResultError as ApiErr } from '../api/client.js';
import { qk } from '../api/queryKeys.js';

// ---------------------------------------------------------------------------
// fetchers
// ---------------------------------------------------------------------------

/**
 * Fetch one project's full detail card (GET /projects/:id). The route answers
 * the success card OR a `{ success:false, error }` failure (missing/out-of-scope,
 * indistinguishable for resource-enumeration hygiene). The failure is converted
 * to a thrown `ApiResultError` so the query is `error` and the view shows
 * not-found — never a half-rendered card.
 */
export async function fetchProjectDetail(projectId: number): Promise<ProjectDetailCard> {
  const result = await apiClient.get<unknown>(`/projects/${projectId}`, {
    command: 'get_1_project',
  });
  const body = projectDetailCardResponseSchema.parse(unwrap(result));
  if (body.success === false) {
    throw new ApiResultError({ code: 'NOT_FOUND', message: body.error || 'Project not found' });
  }
  return body;
}

/** Persist staged edits (PATCH /projects/:id). Returns the raw success body. */
export async function patchProject(
  projectId: number,
  updates: ProjectUpdates,
): Promise<Record<string, unknown>> {
  const result = await apiClient.patch<Record<string, unknown>>(
    `/projects/${projectId}`,
    { updates },
    { command: 'update_project' },
  );
  return unwrap(result);
}

/**
 * E12-F17-T15 — discard an abandoned "New Project" draft (DELETE
 * /projects/:id/draft). The server deletes ONLY a still-empty draft of the caller;
 * `deleted:false` (the user already put work into it) is a normal answer. Never
 * throws — it is fired from Cancel / unmount, where there is nothing to surface.
 */
export async function discardProjectDraft(projectId: number): Promise<boolean> {
  const result = await apiClient.delete<unknown>(`/projects/${projectId}/draft`, {
    command: 'update_project',
    keepalive: true,
  });
  if (!result.ok) return false;
  const parsed = discardDraftResultSchema.safeParse(result.data);
  return parsed.success && parsed.data.deleted;
}

/**
 * Advance / retreat the project stage (POST /projects/:id/advance) — the Back /
 * Next nav. This is the GATED path (PK-QA-1 / PK-QA-3): a Pre-Kickoff(1)→
 * Kick-off(2) move on a project that has no `analysis` artifact answers HTTP 412
 * `CONFIRM_REQUIRED(no_analysis_yet)` unless `confirmed:true` rides along. That
 * 412 is a REAL HTTP error, so it surfaces as a thrown `ApiResultError` whose
 * preserved body the caller reads via `errorBodyOf` → `isConfirmRequired`, then
 * resends with `confirmed:true`. All other transitions (2→3, 3→4, Back) are
 * ungated. The success body is `stageChangeResultSchema` (`success` stripped by
 * the client).
 */
export async function advanceProjectStage(vars: AdvanceStageVars): Promise<StageChangeResult> {
  const result = await apiClient.post<unknown>(
    `/projects/${vars.projectId}/advance`,
    vars.confirmed ? { stage: vars.stage, confirmed: true } : { stage: vars.stage },
    { command: 'update_project' },
  );
  return stageChangeResultSchema.parse(unwrap(result));
}

/**
 * Assign (numeric id) or un-assign (`null`) the PERSISTED sourcing team for a
 * project (PATCH /projects/:id/sourcing-team, E03-F08-T01 / FP-05). Returns the
 * new `{ project_id, sourcing_team_id, sourcing_team }` (the backend also sends
 * `success:true`, which the schema tolerates as optional).
 */
export async function patchProjectSourcingTeam(
  projectId: number,
  sourcingTeamId: number | null,
): Promise<AssignSourcingTeamResponse> {
  const result = await apiClient.patch<unknown>(
    `/projects/${projectId}/sourcing-team`,
    { sourcingTeamId },
    { command: 'assign_sourcing_team' },
  );
  return assignSourcingTeamResponseSchema.parse(unwrap(result));
}

/** Fetch the clients autocomplete cache (GET /clients, id,name ORDER BY name ASC). */
export async function fetchClientsCache(): Promise<ClientOption[]> {
  const result = await apiClient.get<unknown>('/clients', { command: 'clients_cache' });
  return clientsCacheSchema.parse(unwrap(result));
}

// ---------------------------------------------------------------------------
// hooks
// ---------------------------------------------------------------------------

/**
 * Detail-card query keyed by the shared `qk.projects.detail(id)` so a project
 * mutation can invalidate it precisely. Disabled when `id` is null (no project
 * open — the shell never fetches an empty detail).
 */
export function useProjectDetail(
  projectId: number | null,
): UseQueryResult<ProjectDetailCard, ApiErr> {
  return useQuery<ProjectDetailCard, ApiErr>({
    queryKey: qk.projects.detail(projectId ?? 0),
    queryFn: () => fetchProjectDetail(projectId as number),
    enabled: projectId !== null && projectId > 0,
    // A not-found / out-of-scope project is a terminal answer, not a transient
    // failure — do not hammer the endpoint retrying it.
    retry: false,
  });
}

/** Variables for the update mutation. */
export interface UpdateProjectVars {
  readonly projectId: number;
  readonly updates: ProjectUpdates;
}

/** Variables for the stage-advance mutation (POST /:id/advance). */
export interface AdvanceStageVars {
  readonly projectId: number;
  /** Target stage (Back / Next / Confirm-to-Final). The route clamps 1..4. */
  readonly stage: number;
  /**
   * PK-QA-1 soft-gate override — resend with `true` after the user confirms the
   * `no_analysis_yet` dialog. Absent/false on the first attempt.
   */
  readonly confirmed?: boolean;
}

/**
 * Update mutation (PATCH /projects/:id). On success it invalidates BOTH the
 * project's detail card (so the header re-reads the persisted values) and the
 * projects LIST (so a role/client rename shows in the dashboard) — and, when a
 * client was created/linked, the clients cache so the autocomplete picks it up.
 */
export function useUpdateProject(): UseMutationResult<
  Record<string, unknown>,
  ApiErr,
  UpdateProjectVars
> {
  const queryClient = useQueryClient();
  return useMutation<Record<string, unknown>, ApiErr, UpdateProjectVars>({
    mutationFn: ({ projectId, updates }) => patchProject(projectId, updates),
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: qk.projects.lists() });
      void queryClient.invalidateQueries({ queryKey: qk.clients.all });
    },
  });
}

/**
 * Stage-advance mutation (POST /:id/advance — the Back / Next nav). Keyed by the
 * `AdvanceStageVars`; on success it invalidates the project's detail card (the
 * stepper / nav re-read the new stage) AND the projects list (the dashboard row's
 * stage). PK-QA-1: a 412 `CONFIRM_REQUIRED(no_analysis_yet)` rejects into
 * `onError`/the awaited `mutateAsync` catch, where the caller offers the confirm
 * and resends with `confirmed:true` (see {@link advanceProjectStage}).
 */
export function useAdvanceStage(): UseMutationResult<StageChangeResult, ApiErr, AdvanceStageVars> {
  const queryClient = useQueryClient();
  return useMutation<StageChangeResult, ApiErr, AdvanceStageVars>({
    mutationFn: advanceProjectStage,
    onSuccess: (_data, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
      void queryClient.invalidateQueries({ queryKey: qk.projects.lists() });
    },
  });
}

/**
 * Assign / un-assign mutation (PATCH /projects/:id/sourcing-team, E03-F08-T01).
 * The variable is the team id (numeric) or `null` to clear the assignment. On
 * success it invalidates the project's detail card so the persisted
 * `sourcing_team` / `sourcing_team_id` are re-read — the same invalidate pattern
 * as {@link useUpdateProject} (no bespoke optimistic layer).
 */
export function useAssignSourcingTeam(
  projectId: number,
): UseMutationResult<AssignSourcingTeamResponse, ApiErr, number | null> {
  const queryClient = useQueryClient();
  return useMutation<AssignSourcingTeamResponse, ApiErr, number | null>({
    mutationFn: (sourcingTeamId) => patchProjectSourcingTeam(projectId, sourcingTeamId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    },
  });
}

/** Clients autocomplete cache query (T05). Cached under `qk.clients.list()`. */
export function useClientsCache(enabled: boolean = true): UseQueryResult<ClientOption[], ApiErr> {
  return useQuery<ClientOption[], ApiErr>({
    queryKey: qk.clients.list(),
    queryFn: () => fetchClientsCache(),
    enabled,
    staleTime: 60_000,
  });
}
